import LoadingIndicator from '@/shared/components/loading-indicator/index.vue';
import ModalBox from "@/shared/components/modal-box/index.vue";
import { TrustAPI } from '@/shared/modules/trust-api';
import { AppInsights } from '@/trust-admin-app/appInsights';
import store from '@/trust-admin-app/store';
import { EventEmitter } from 'events';
import { Inject, Options, Prop, Vue, Watch } from "vue-property-decorator";
import debounce from 'lodash/debounce';
import V3DatePicker from 'vue3-datepicker';
import ErrorIndicator from '@/shared/components/error-indicator/index.vue';
import moment from 'moment';
import NumberFormattingMixin from '@/shared/mixins/number-formatting';
import { SecurityGroupMemberView, Supervisor, TrustTeamMemberRequest, TrustTeamMemberView, UserGroup } from '@/shared/modules/typescript-trustapi-client/models';

@Options({
  components: {
    ModalBox,
    V3DatePicker,
    LoadingIndicator,
    ErrorIndicator
  },
  mixins: [NumberFormattingMixin]
})
export default class AddEditMemberModal extends Vue {
  private ai = new AppInsights;

  @Inject({ from: 'apiService', default: new TrustAPI() })
  private apiService!: TrustAPI;

  @Inject({ from: 'userGroups', default: [] })
  private userGroups!: UserGroup[];

  @Prop({ default: null })
  teamMember!: TrustTeamMemberView;

  private loadingContent = false;
  private errorLoadingContent = false;
  private submitting = false;
  private isOpen = false;
  private arrowCounter = -1;
  private search = '';
  private isSearching = false;
  private srchDebounced: any = null;

  private memberList: SecurityGroupMemberView[] = [];
  private supervisorList: Supervisor[] = [];
  private formData: TrustTeamMemberView = {};

  private startDateForTimesheet: Date = null as any;
  private endDateForTimesheet: Date = null as any;

  @Watch('search')
  private onKeywordChange(): void {
    if (this.memberList.filter(x => x.displayName == this.search).length == 0) {
      this.formData.id = 0;
      setTimeout(this.srchDebounced.cancel, 0);
      setTimeout(this.srchDebounced, 0);
    }
  }

  mounted(): void {
    window.addEventListener('click', this.handleClickOutside);
    this.srchDebounced = debounce(() => {
      this.searchUser();
    }, 1000);
  }

  beforeUnmount(): void {
    window.removeEventListener('click', this.handleClickOutside);
  }

  created(): void {
    this.init();
  }

  private async init(): Promise<void> {
    try {
      this.loadingContent = true;
      this.errorLoadingContent = false;
      this.memberList = [];

      await this.preloadData();

      this.formData = JSON.parse(JSON.stringify(this.teamMember));

      this.formData.id = this.formData.id ?? 0;
      this.formData.userGroup = this.formData.userGroup ?? {
        id: null
      };
      this.formData.supervisor = this.formData.supervisor ?? {
        id: undefined
      };

      if (this.editMode) {
        this.startDateForTimesheet = this.formData.startDate ? new Date(
          moment(this.formData.startDate).format('YYYY-MM-DD 00:00:00')
        ) : null as any;
        this.endDateForTimesheet = this.formData.endDate ? new Date(
          moment(this.formData.endDate).format('YYYY-MM-DD 00:00:00')
        ) : null as any;
      }

    } catch (err) {
      this.errorLoadingContent = true;
    } finally {
      this.loadingContent = false;
    }
  }

  private async saveTrustTeamMember(): Promise<void> {
    try {
      if (!this.validForm && !this.isFormDirty) {
        throw new Error('Invalid Data Filled');
      }
      this.submitting = true;
      const memReq: TrustTeamMemberRequest = {
        id: this.formData.id,
        externalId: this.formData.externalId ?? null,
        firstName: this.formData.firstName,
        lastName: this.formData.lastName,
        displayName: this.formData.displayName,
        email: this.formData.email,
        mobilePhone: this.formData.mobilePhone,
        securityGroups: this.formData.securityGroups,
        userGroupId: this.formData.userGroup.id,
        startDate: this.startDateForTimesheet ? new Date(this.startDateForTimesheet).toISOString() : null,
        endDate: this.endDateForTimesheet ? new Date(this.endDateForTimesheet).toISOString() : null,
        supervisorId: this.formData.supervisor?.id ?? null,
        chargeAmount: this.formData.chargeAmount
      };
      
      await this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberSaveTrustTeamMemberPost(memReq);

      if (memReq.id == 0) {
        this.eventCtrl.emit('show-snackbar', 'success', 'Member added successfully');
        this.eventCtrl.emit('reload-trust-team');
      } else {
        this.$emit('detailsUpdated');
        this.eventCtrl.emit('show-snackbar', 'success', 'Member updated successfully');
        this.eventCtrl.emit('reload-investigator-dashboard');
      }
      this.closeModal();
    } catch (err) {
      this.eventCtrl.emit('show-snackbar', 'error', 'Unable to process request');
    } finally {
      this.submitting = false;
    }
  }

  private async preloadData(): Promise<boolean> {
    try {
      const resp: any[] = await Promise.all([
        this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberGetSupervisorsGet(),
      ]);
      this.supervisorList = resp[0].data.data as Supervisor[];
      return Promise.resolve(false);
    } catch (err) {
      return Promise.reject(true);
    }
  }

  private closeModal() {
    this.$emit('closeAddEditMemberModal');
  }

  private async searchUser(): Promise<void> {
    try {
      this.isSearching = true;
      const resp: any = await this.apiService.TrustTeamMemberApi?.apiV2TrustTeamMemberSearchSecurityGroupMembersGet(
        this.search,
        true
      );
      this.memberList = resp.data.data as SecurityGroupMemberView[];
      this.isOpen = this.memberList.length > 0;
    } catch (err) {
      this.isOpen = false;
    } finally {
      this.isSearching = false;
    }
  }

  private selectMember(m: SecurityGroupMemberView): void {
    this.isOpen = false;
    this.search = m.displayName;

    if (!this.editMode) {
      this.formData.externalId = m.id;
      this.formData.displayName = m.displayName;
      this.formData.firstName = m.givenName;
      this.formData.lastName = m.surname;
      this.formData.email = m.email;
      this.formData.mobilePhone = m.mobilePhone;
      this.formData.securityGroups = m.securityGroups;
    }
  }

  private onArrowDown() {
    if (this.arrowCounter < this.memberList.length) {
      this.arrowCounter = this.arrowCounter + 1;
    }
  }

  private onArrowUp(): void {
    if (this.arrowCounter > 0) {
      this.arrowCounter = this.arrowCounter - 1;
    }
  }

  private onEnter() {
    if (this.memberList.length > 0) {
      this.selectMember(
        this.memberList[this.arrowCounter === -1 ? 0 : this.arrowCounter]
      );
    }
    this.isOpen = false;
  }

  private handleClickOutside(evt: any): void {
    if (this.$el === (evt.target)) {
      this.isOpen = false;
      this.arrowCounter = -1;
    }
  }

  private highlightWord(word: string): string {
    try {
      const regex = new RegExp('(' + this.search + ')', 'ig');
      return word.replace(regex, '<mark>$1</mark>');
    } catch (err) {
      return word;
    }
  }

  get validForm(): boolean {

    const chk1 = !!this.formData.externalId;
    const chk2 = !!this.formData.userGroup?.id;
    const chk3 = this.formData.userGroup.id == 1 ||
      (this.formData.userGroup.id > 1 &&
        !isNaN(this.formData.chargeAmount) &&
        this.formData.chargeAmount >= 0 &&
        !!this.startDateForTimesheet
      );
    const chk4 = !!this.endDateForTimesheet;
    try {
      if (this.teamMember.userGroup?.id > 1 && this.formData.userGroup?.id == 1) {
        return chk1 && chk2 && chk3 && chk4;
      } else {
        return chk1 && chk2 && chk3;
      }

    } catch {
      return false;
    }
  }

  get isFormDirty(): boolean {
    if (this.editMode) {

      const c_Role = this.teamMember.userGroup.id !== this.formData.userGroup.id;
      const c_ChargeAmount = this.teamMember.chargeAmount !== this.formData.chargeAmount;
      const c_Supervisor = this.teamMember.supervisor?.id !== this.formData.supervisor.id;
      const c_StartDate = moment(this.teamMember?.startDate).unix() !==
        moment(new Date(this.startDateForTimesheet).toISOString()).unix();
      const c_EndDate = moment(this.teamMember?.endDate).unix() !==
        moment(new Date(this.endDateForTimesheet).toISOString()).unix();

      // console.log(this.teamMember?.endDate, this.endDateForTimesheet);

      if (this.teamMember.userGroup?.id > 1 && this.formData.userGroup?.id == 1) {
        return c_Role || c_StartDate || c_EndDate;
      } else {
        return c_Role || c_ChargeAmount || c_Supervisor || c_StartDate || c_EndDate;
      }

    } else {
      return !!this.formData.externalId;
    }
  }

  get editMode(): boolean {
    try {
      return this.teamMember.id > 0;
    } catch {
      return false;
    }
  }

  get eventCtrl(): EventEmitter {
    return store.getters.getEventController;
  }

}

